import { fromUtf8, toBase64 } from './bytes';
import { blobAad, open } from './crypto';
import type { CloudManifest, CloudManifestFormat, ManifestEnvelope } from './manifest';
import { openManifestBody } from './manifest';
import { bringForward } from './manifestRecord';
import type { SealedRecord } from './manifestRecord';
import type { ReadCache } from './cache';
import { memoryCache } from './cache';

/**
 * Reading the vault from a browser.
 *
 * The shape of the work is four operations — list paths under a prefix, read
 * one file, read many, and know the version everything was read at — because
 * that is the entire vault dependency of the plan board. Porting the board
 * means swapping what sits behind this seam, not rewriting it.
 *
 * Cost model: one manifest doc, then the sealed body, then a blob per file.
 * The body and every opened file are cached against a version that only ever
 * moves forward, so the steady state is a single version probe, and a tab a
 * few versions behind comes forward by the records the pushes left.
 */

/** getVaultUploadUrls refuses more than this per call. */
const MAX_URLS_PER_CALL = 256;

/** How many pages of records one sync follows; the backend serves a few hundred per page. */
const MAX_RECORD_PAGES = 50;

/** Grant-actor signed URLs are minted at a 5-minute TTL against the
 *  custodian's 15. Nothing may hold one longer than it lives — the cache
 *  keeps decrypted CONTENT, never a URL. */
const URL_BATCH_CONCURRENCY = 8;

export interface VaultBackend {
  /** Runs a callable with this session's credentials attached. */
  call<T>(name: string, data: Record<string, unknown>): Promise<T>;
  /** Fetches blob bytes from a signed URL. */
  fetchBlob(url: string): Promise<Uint8Array>;
}

export interface VaultReadResult {
  path: string;
  text: string;
}

/** Reported as batches land, so a cold load can count DOWN rather than
 *  spin. Cached files never reach the network, so `total` is what actually
 *  has to be fetched — not how many were asked for. */
export type ReadProgress = (done: number, total: number) => void;

export class VaultUnavailableError extends Error {}

/**
 * The bytes never arrived — network, CORS, or storage refusing the read.
 *
 * Split from the key case because the two are fixed in completely different
 * places and were, for one evening, indistinguishable: an origin missing from
 * the bucket's CORS list reported itself as "could not read the vault", which
 * reads as "your key is wrong" and sends the reader after the wrong problem.
 */
export class VaultUnreachableError extends VaultUnavailableError {}

/** The bytes arrived and this key did not open them. */
export class VaultKeyMismatchError extends VaultUnavailableError {}

interface CachedManifest {
  manifest: CloudManifest;
  format: CloudManifestFormat;
}

export class CloudVault {
  private manifest: CloudManifest | null = null;
  private format: CloudManifestFormat = 'v1';
  private version = 0;
  private envelope: ManifestEnvelope | null = null;
  private serverNowMs: number | null = null;

  constructor(
    private readonly backend: VaultBackend,
    private readonly dataKey: Uint8Array,
    private readonly cache: ReadCache = memoryCache(),
  ) {}

  get manifestVersion(): number {
    return this.version;
  }

  /** The plaintext envelope as last fetched. A writer needs it whole: the
   *  two fields it must echo verbatim live here, not in the sealed body. */
  get currentEnvelope(): ManifestEnvelope | null {
    return this.envelope;
  }

  get currentManifest(): CloudManifest | null {
    return this.manifest;
  }

  /** The backend's clock when the last sync reached it — what an edit written right after is
   *  dated by. Null when the backend did not say, and never this browser's clock instead. */
  get serverTimeMs(): number | null {
    return this.serverNowMs;
  }

  /** The wire format the manifest was read in. A write FOLLOWS it, so the web
   *  never cuts the vault over on its own — the fleet moves when a device
   *  deliberately upgrades and everyone else follows by reading. */
  get manifestFormat(): CloudManifestFormat {
    return this.format;
  }

  /** Applied by the writer after a CAS it won, so the next read does not
   *  have to round-trip for state it already knows. */
  adoptWrittenManifest(
    manifest: CloudManifest,
    envelope: ManifestEnvelope,
    format: CloudManifestFormat,
  ): void {
    this.manifest = manifest;
    this.envelope = envelope;
    this.version = envelope.manifestVersion;
    this.format = format;
  }

  /**
   * Opens a manifest already in the cache at a version somebody else has
   * vouched for, so a tab can paint before the network answers.
   *
   * The envelope stays null, because the cache holds manifests and not
   * envelopes and inventing one would be a lie about what was read. Only the
   * writer needs it, and the writer syncs before it builds a push — so a
   * warm-started vault is safe to write through, and the CAS covers the rest.
   *
   * False when the version is not cached, which is the ordinary answer on a
   * browser that has not held this vault before.
   */
  async warmStart(version: number): Promise<boolean> {
    const cached = await this.cachedManifest(version);
    if (!cached) return false;
    this.manifest = cached.manifest;
    this.format = cached.format;
    this.version = version;
    return true;
  }

  /**
   * Brings the local picture up to date. Cheap when nothing moved: the
   * envelope is one small document, and a version we have already opened
   * skips the body entirely.
   */
  async sync(): Promise<void> {
    const { envelope, serverNowMs } = await this.backend.call<{
      envelope: ManifestEnvelope | null;
      serverNowMs?: number;
    }>('getVaultManifest', {});
    this.serverNowMs = typeof serverNowMs === 'number' ? serverNowMs : null;
    if (!envelope) {
      this.manifest = { blobs: {}, files: {} };
      this.envelope = null;
      this.version = 0;
      return;
    }

    this.envelope = envelope;
    if (this.manifest && envelope.manifestVersion === this.version) return;

    const cached = await this.cachedManifest(envelope.manifestVersion);
    if (cached) {
      this.manifest = cached.manifest;
      this.format = cached.format;
      this.version = envelope.manifestVersion;
      return;
    }

    // A tab a few versions behind comes forward by the records since, without the body. A change of
    // format sends no record, so the format read with the manifest still holds.
    if (this.manifest && this.version > 0 && this.version < envelope.manifestVersion) {
      const forward = await this.forward(this.manifest, this.version, envelope.manifestVersion);
      if (forward) return this.take(forward, this.format, envelope.manifestVersion);
    }

    const bodyBase64 = envelope.body ?? (await this.fetchBodyBlob(envelope));
    const opened = openManifestBody(this.dataKey, bodyBase64, envelope.snapshotVersion);
    if (!opened) {
      throw new VaultKeyMismatchError('the manifest did not open with this key');
    }
    let { manifest } = opened;
    // Behind a fenced snapshot the records after it are the only copy of what changed, so a chain
    // that does not reach leaves nothing to read the vault by.
    const snapshotAt = envelope.snapshotVersion ?? envelope.manifestVersion;
    if (snapshotAt < envelope.manifestVersion) {
      const forward = await this.forward(manifest, snapshotAt, envelope.manifestVersion);
      if (!forward) {
        throw new VaultUnavailableError(
          `the records after the snapshot at v${snapshotAt} do not reach v${envelope.manifestVersion}`,
        );
      }
      manifest = forward;
    }
    await this.take(manifest, opened.format, envelope.manifestVersion);
  }

  private async take(manifest: CloudManifest, format: CloudManifestFormat, version: number): Promise<void> {
    this.format = format;
    this.manifest = manifest;
    this.version = version;
    await this.cache.putManifest(version, { manifest, format } satisfies CachedManifest);
  }

  /** `manifest`, the vault at `from`, brought to `upTo` by the records, page by page — or null. */
  private async forward(manifest: CloudManifest, from: number, upTo: number): Promise<CloudManifest | null> {
    const chain: SealedRecord[] = [];
    let last = from;
    for (let page = 0; page < MAX_RECORD_PAGES && last < upTo; page++) {
      const { records } = await this.backend.call<{ records?: SealedRecord[] }>('getVaultManifest', {
        recordsAfter: last,
      });
      if (!records || records.length === 0) break;
      for (const record of records) {
        if (record.version !== last + 1) break;
        chain.push(record);
        last = record.version;
      }
      if (last !== records[records.length - 1]!.version) break;
    }
    return bringForward(this.dataKey, manifest, from, upTo, chain);
  }

  /** The manifest the cache holds at `version`, with the format a write follows. An entry cached
   *  without its format is read again from the body: taken as v1, a write would seal a v2 vault
   *  as v1, and that inversion drops every path sharing a blob. */
  private async cachedManifest(version: number): Promise<CachedManifest | null> {
    const cached = (await this.cache.getManifest(version)) as Partial<CachedManifest> | null;
    if (!cached?.manifest || (cached.format !== 'v1' && cached.format !== 'v2')) return null;
    return cached as CachedManifest;
  }

  /** Vault-relative paths under a prefix, sorted. */
  listPaths(prefix: string): string[] {
    return Object.keys(this.requireManifest().files)
      .filter((path) => path.startsWith(prefix))
      .sort();
  }

  /** The blob version recorded for a path, or null if the vault has no such
   *  file. Version 1 means the entry was created rather than updated. */
  fileVersion(path: string): number | null {
    const manifest = this.requireManifest();
    const blobId = manifest.files[path];
    if (!blobId) return null;
    return manifest.blobs[blobId]?.version ?? null;
  }

  /** sha256 of a path's plaintext, hex, as the manifest records it — or null
   *  if the vault has no such file. */
  contentHash(path: string): string | null {
    const manifest = this.requireManifest();
    const blobId = manifest.files[path];
    return (blobId && manifest.blobs[blobId]?.contentHash) || null;
  }

  /**
   * A file's bytes as stored, for media: neither decoded nor cached, since a
   * recording is fetched to be played, not kept. Null when the vault has no
   * such file or its blob will not open.
   */
  async readBytes(path: string): Promise<Uint8Array | null> {
    const manifest = this.requireManifest();
    const blobId = manifest.files[path];
    const entry = blobId ? manifest.blobs[blobId] : undefined;
    if (!blobId || !entry) return null;
    const { urls } = await this.backend.call<{ urls: Record<string, string> }>(
      'getVaultUploadUrls',
      { blobIds: [blobId], mode: 'read' },
    );
    const url = urls[blobId];
    if (!url) return null;
    const sealed = await this.backend.fetchBlob(url);
    return (
      open(this.dataKey, blobAad(blobId, entry.version), sealed) ??
      open(this.dataKey, blobAad(blobId, entry.version + 1), sealed)
    );
  }

  async readFile(path: string): Promise<string | null> {
    const [result] = await this.readFiles([path]);
    return result?.text ?? null;
  }

  /**
   * Reads many files with as few round trips as the backend allows: one URL
   * call per 256 blobs, then the bytes in parallel. Paths the manifest does
   * not know are dropped rather than throwing — a board asking for
   * `_tags.md` in a vault that has none should render untinted, not fail.
   */
  async readFiles(paths: string[], onProgress?: ReadProgress): Promise<VaultReadResult[]> {
    const manifest = this.requireManifest();
    const wanted: { path: string; blobId: string; version: number }[] = [];
    const results: VaultReadResult[] = [];

    for (const path of paths) {
      const blobId = manifest.files[path];
      const entry = blobId ? manifest.blobs[blobId] : undefined;
      if (!blobId || !entry) continue;

      const cached = await this.cache.getFile(blobId, entry.version);
      if (cached !== null) results.push({ path, text: cached });
      else wanted.push({ path, blobId, version: entry.version });
    }

    onProgress?.(0, wanted.length);
    let fetched = 0;
    for (let i = 0; i < wanted.length; i += MAX_URLS_PER_CALL) {
      const batch = wanted.slice(i, i + MAX_URLS_PER_CALL);
      const { urls } = await this.backend.call<{ urls: Record<string, string> }>(
        'getVaultUploadUrls',
        { blobIds: batch.map((b) => b.blobId), mode: 'read' },
      );
      results.push(
        ...(await this.openBatch(batch, urls, (n) => {
          fetched += n;
          onProgress?.(fetched, wanted.length);
        })),
      );
    }

    const order = new Map(paths.map((path, index) => [path, index]));
    return results.sort((a, b) => (order.get(a.path) ?? 0) - (order.get(b.path) ?? 0));
  }

  private async openBatch(
    batch: { path: string; blobId: string; version: number }[],
    urls: Record<string, string>,
    onSlice?: (count: number) => void,
  ): Promise<VaultReadResult[]> {
    const out: VaultReadResult[] = [];
    for (let i = 0; i < batch.length; i += URL_BATCH_CONCURRENCY) {
      const slice = batch.slice(i, i + URL_BATCH_CONCURRENCY);
      const opened = await Promise.all(
        slice.map(async ({ path, blobId, version }) => {
          const url = urls[blobId];
          if (!url) return null;
          const sealed = await this.backend.fetchBlob(url);
          // A push that uploads its blobs and then loses the manifest CAS leaves storage
          // sealed at version + 1 while the manifest still records version. Those bytes are
          // the vault's own — authenticated by the data key — so they are read rather than
          // skipped, exactly one version ahead and no further: anything beyond that is a blob
          // this reader cannot vouch for. Repairing the manifest is a push's job, and this
          // reader has no push.
          const plain =
            open(this.dataKey, blobAad(blobId, version), sealed) ??
            open(this.dataKey, blobAad(blobId, version + 1), sealed);
          if (!plain) {
            // Loud on purpose: a path dropped in silence reads as a file that does not exist,
            // on a surface with nothing else to say so.
            console.warn(
              `vault: ${path} could not be opened (blob ${blobId}, manifest version ${version}) — skipped`,
            );
            return null;
          }
          const text = fromUtf8(plain);
          // Under the version the manifest records, which is the key the next read asks with.
          await this.cache.putFile(blobId, version, text);
          return { path, text };
        }),
      );
      out.push(...opened.filter((r): r is VaultReadResult => r !== null));
      onSlice?.(slice.length);
    }
    return out;
  }

  private async fetchBodyBlob(envelope: ManifestEnvelope): Promise<string> {
    if (!envelope.bodyBlobId) {
      throw new VaultUnavailableError('the manifest carries neither a body nor a pointer to one');
    }
    const { urls } = await this.backend.call<{ urls: Record<string, string> }>(
      'getVaultUploadUrls',
      { blobIds: [envelope.bodyBlobId], mode: 'read' },
    );
    const url = urls[envelope.bodyBlobId];
    if (!url) throw new VaultUnavailableError('no read url for the manifest body');
    // Sealed under the manifest AAD when it was written, and stored as-is —
    // unlike a file blob, it takes no blobAad step on the way back.
    return toBase64(await this.backend.fetchBlob(url));
  }

  private requireManifest(): CloudManifest {
    if (!this.manifest) throw new VaultUnavailableError('sync() has not run');
    return this.manifest;
  }
}
